import React, { useContext, useEffect, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import CreatableSelect from 'react-select/creatable';
import toast from 'react-hot-toast';
import ReactTooltip from 'react-tooltip';
import { useMutation } from '@apollo/react-hooks';
import colors from '../../styles/colors';
import Button from '../Button';
import Icon from '../Icon';
import { IconEnum } from '../Icons';
import { UserContext } from '../../context/UserContext';
import { UPDATE_ATTACHED_TAGS } from '../../graphql/queries';

const MessageDetails = styled.div`
  display: flex;
  font-size: 1.2rem;
  color: ${colors.grey3};
  padding: 0 5px;
`;

const TooltipStyles = createGlobalStyle`
.__react_component_tooltip{
      display: inline-block !important;
      max-width: 300px;
    };
    .css-12jo7m5{
      font-size:13px !important;
    };
    .css-1pahdxg-control{
      border-color: ${colors.grey3} !important;
      box-shadow: none !important;
      min-height: 40px;
    }
`;

const Form = styled.form`
  padding: 20px 15px;
  max-width: 300px;
  width: calc(75vw - 20px);
`;

export default function AttachContactTagModal({
  phoneNumber,
  attachedTags,
  setAttachedTags,
  close,
}) {
  const { user, setUser } = useContext(UserContext);
  const [tags, setTags] = useState([]);
  const [initiallyAttachedTags] = useState(attachedTags);
  const [selectedTags, setSelectedTags] = useState(
    attachedTags?.map((tag) => {
      return {
        label: tag,
        value: tag,
      };
    }) || [],
  );

  const setValidateTagValue = (newValue = '') => {
    const regex = /^[a-z0-9-]{1,}$/;
    const isValid = regex?.test(newValue);
    if (isValid) {
      const tagsWithNew = [
        ...selectedTags,
        { label: newValue, value: newValue },
      ];
      setTags((old) => [...old, { label: newValue, value: newValue }]);
      return setSelectedTags(tagsWithNew);
    }
    toast.error('Please enter a valid contact tag');
  };

  const [updateAttatchedTags, { loading: attachedTagsUpdating }] = useMutation(
    UPDATE_ATTACHED_TAGS,
    {
      onCompleted(data) {
        const attachedTags = data.updateContactTags.tags;
        const oldTagOptions = user.organization.tags;
        const newTags = attachedTags.filter((i) => !oldTagOptions.includes(i));
        const sortedTags = [...user.organization.tags, ...newTags].sort(
          (a, b) => {
            if (a > b) {
              return 1;
            }
            return -1;
          },
        );
        setAttachedTags(attachedTags);
        user.organization.tags = sortedTags;
        const updatedUser = { ...user };
        setUser(() => updatedUser);
        toast.success('Updated Successfully');
        close();
      },
    },
  );

  const attachTagsToContact = (phone, updatedTags) => {
    const initialItems = initiallyAttachedTags || [];
    const updatedItems = updatedTags?.map((i) => i.label) || [];
    const intitalNotChanged = initialItems.every((item) => updatedItems.includes(item));
    const updatedNotChanged = updatedItems.every((item) => initialItems.includes(item));

    if (!(intitalNotChanged && updatedNotChanged)) {
      return updateAttatchedTags({
        variables: {
          phoneNumber: phone,
          tags: updatedTags?.map((tag) => tag.label) || [],
        },
      });
    }

    toast("You have'nt attached or removed any tag.");
  };

  useEffect(() => {
    if (user?.organization?.tags) {
      const tagsData = user?.organization?.tags.map((tag) => {
        return {
          label: tag,
          value: tag,
        };
      });
      setTags(tagsData);
    }
  }, [user]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <TooltipStyles />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '10px',
          width: 'max-content',
        }}
        data-tip="Tags should contain only lower-case letters, numbers, and hyphens (no other special characters or spaces allowed)."
      >
        <MessageDetails>Tags</MessageDetails>{' '}
        <Icon size={10} margin="0 5px" icon={IconEnum.infoRegular} />
      </div>

      <CreatableSelect
        onChange={(updatedTags) => setSelectedTags(updatedTags || [])}
        options={tags}
        isClearable={false}
        isMulti
        value={selectedTags}
        onCreateOption={setValidateTagValue}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          margin="30px 0 10px 0"
          text="UPDATE TAGS"
          type="button"
          width="150px"
          disabled={attachedTagsUpdating}
          loading={attachedTagsUpdating}
          onClick={() => attachTagsToContact(phoneNumber, selectedTags)}
        />
      </div>

      <ReactTooltip
        place="right"
        type="dark"
        effect="solid"
        clickable={false}
      />
    </Form>
  );
}
